import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { DecimalPipe } from '@angular/common';

import { MatButton } from '@angular/material/button';
import { MatOption } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerInput,
  MatDatepickerToggle,
} from '@angular/material/datepicker';
import {
  MatFormField,
  MatLabel,
  MatSuffix,
} from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatIcon } from '@angular/material/icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatSelect } from '@angular/material/select';
import { MatTooltip } from '@angular/material/tooltip';
import { ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@next/core-lib/i18n';
import { disabled, form, FormField, min, required } from '@angular/forms/signals';
import { DateService } from '@app/shared/services/date.service';
import { toNativeDate } from '@app/shared/utils';
import { IrrigationRegisterEvent } from '@app/irrigations/models/ui-models';
import { CreateIrrigationType } from '@app/irrigations/models/models';
import { IrrigationTypePipe } from '@app/irrigations/pipes/irrigation-type.pipe';

interface RegisterIrrigationFormData {
  amountInMillimeters: number | null;
  startDate: Date;
  endDate: Date;
  startHour: number;
  endHour: number;
  type: CreateIrrigationType;
}

@Component({
  selector: 'app-irrigation-register-form',
  imports: [
    DecimalPipe,
    MatButton,
    MatDatepicker,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatFormField,
    MatIcon,
    MatInput,
    MatLabel,
    MatOption,
    MatRadioButton,
    MatRadioGroup,
    MatSelect,
    MatTooltip,
    MatSuffix,
    ReactiveFormsModule,
    TranslatePipe,
    FormField,
    IrrigationTypePipe,
  ],
  templateUrl: './irrigation-dialog-register-form.component.html',
  styleUrl: './irrigation-dialog-register-form.component.css',
})
export class IrrigationDialogRegisterFormComponent {
  private readonly dateService = inject(DateService);

  readonly actionDate = input<Date>();
  readonly readOnly = input(false);
  readonly showPastYearReadonlyTooltip = input(false);
  readonly irrigationRegistered = output<IrrigationRegisterEvent>();

  protected readonly hours: number[] = Array.from({ length: 24 }, (_, index) => index);
  protected readonly irrigationTypes: CreateIrrigationType[] = ['Sprinkler', 'Drip'];

  private readonly now = this.dateService.now();
  private readonly defaultStartHour = this.getDefaultStartHour(this.now);

  private readonly registerIrrigationModel = signal<RegisterIrrigationFormData>({
    amountInMillimeters: null,
    startDate: this.now,
    endDate: this.now,
    startHour: this.defaultStartHour,
    endHour: this.getDefaultEndHour(this.defaultStartHour),
    type: 'Sprinkler',
  });

  protected readonly registerIrrigationForm = form(this.registerIrrigationModel, (schemaPath) => {
    disabled(schemaPath.startDate, () => this.readOnly());
    disabled(schemaPath.endDate, () => this.readOnly());
    disabled(schemaPath.startHour, () => this.readOnly());
    disabled(schemaPath.endHour, () => this.readOnly());
    disabled(schemaPath.amountInMillimeters, () => this.readOnly());
    disabled(schemaPath.type, () => this.readOnly());

    required(schemaPath.startDate);
    required(schemaPath.endDate);
    required(schemaPath.startHour);
    required(schemaPath.endHour);
    required(schemaPath.type);

    required(schemaPath.amountInMillimeters);
    min(schemaPath.amountInMillimeters, 1);
  });

  protected readonly isSubmitDisabled = computed(() => {
    return this.readOnly() || this.registerIrrigationForm().invalid() || this.hasInvalidDateRange() || this.hasInvalidDateTimeRange();
  });

  protected readonly hasInvalidDateRange = computed(() => {
    const startDate = toNativeDate(this.registerIrrigationForm.startDate().value());
    const endDate = toNativeDate(this.registerIrrigationForm.endDate().value());
    if (!startDate || !endDate) {
      return false;
    }
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return start.getTime() > end.getTime();
  });

  protected readonly hasInvalidDateTimeRange = computed(() => {
    const startDate = toNativeDate(this.registerIrrigationForm.startDate().value());
    const endDate = toNativeDate(this.registerIrrigationForm.endDate().value());
    const startHour = this.registerIrrigationForm.startHour().value();
    const endHour = this.registerIrrigationForm.endHour().value();

    if (!startDate || !endDate) {
      return false;
    }

    const startedOn = this.buildUtcDateTime(startDate, startHour);
    const endedOn = this.buildUtcDateTime(endDate, endHour);

    return startedOn.getTime() >= endedOn.getTime();
  });

  constructor() {
    // Update the date and hours when actionDate input is set or changed.
    effect(() => {
      const actionDate = this.actionDate();
      if (actionDate) {
        const startHour = this.getDefaultStartHour(actionDate);
        const endHour = this.getDefaultEndHour(startHour);
        this.registerIrrigationModel.update((current) => ({
          ...current,
          startDate: actionDate,
          endDate: actionDate,
          startHour,
          endHour,
        }));
      }
    });
  }

  protected registerIrrigation(event: Event): void {
    event.preventDefault();
    if (this.readOnly()) {
      return;
    }

    const startDate = toNativeDate(this.registerIrrigationForm.startDate().value());
    const endDate = toNativeDate(this.registerIrrigationForm.endDate().value());
    const startHour = this.registerIrrigationModel().startHour;
    const endHour = this.registerIrrigationModel().endHour;

    if (!startDate || !endDate) {
      return;
    }

    const startedOn = this.buildUtcDateTime(startDate, startHour);
    const endedOn = this.buildUtcDateTime(endDate, endHour);

    if (startedOn.getTime() >= endedOn.getTime()) {
      return;
    }

    const amountInMillimeters = this.registerIrrigationModel().amountInMillimeters;
    const type = this.registerIrrigationModel().type;

    if (amountInMillimeters === null) {
      return;
    }

    this.irrigationRegistered.emit({
      cropYear: this.dateService.currentYear(),
      startedOn,
      endedOn,
      amountInMillimeters,
      type,
    });

    this.resetForm();
  }

  private resetForm(): void {
    const actionDate = this.actionDate();
    const date = actionDate || this.dateService.now();
    const startHour = this.getDefaultStartHour(date);
    this.registerIrrigationModel.set({
      amountInMillimeters: null,
      startDate: date,
      endDate: date,
      startHour,
      endHour: this.getDefaultEndHour(startHour),
      type: 'Sprinkler',
    });
  }

  private buildUtcDateTime(date: Date, hour: number): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), hour, 0));
  }

  private getDefaultStartHour(date: Date): number {
    const now = this.dateService.now();
    const isToday = this.dateService.isDateToday(date);

    if (!isToday) {
      return 8; // Default to 8:00 for past dates
    }

    if (now.getMinutes() < 30) {
      return Math.min(now.getHours(), 22);
    }

    return Math.min(now.getHours() + 1, 22);
  }

  private getDefaultEndHour(startHour: number): number {
    return Math.min(startHour + 4, 23);
  }
}
